// /src/firebase.js
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult as _getRedirectResult,
  onAuthStateChanged as _onAuthStateChanged,
  signOut as _signOut,
} from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';

// This is the Web App you just created (brand-resources) under the
// "Meltplan Website" Firebase project
const firebaseConfig = {
  apiKey: 'AIzaSyCkOqO8KtjmTOdj7KYW5hBad6H1zu93rzg',
  authDomain: 'meltplan-website.firebaseapp.com',
  projectId: 'meltplan-website',
  storageBucket: 'meltplan-website.firebasestorage.app',
  messagingSenderId: '433989517019',
  appId: '1:433989517019:web:f84061f48393e55ff396fe',
  measurementId: 'G-SD0P8JK4ED',
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const storage = getStorage(app);
const db = getFirestore(app);
// Cloud Functions live in us-central1 (see functions/index.js setGlobalOptions).
const functions = getFunctions(app, 'us-central1');

// Local development against the Firebase emulator suite. Auth stays on the real
// project (real Google sign-in works on localhost); only Firestore + Functions
// point at the local emulators. Enable with REACT_APP_USE_EMULATORS=true.
if (process.env.REACT_APP_USE_EMULATORS === 'true') {
  try {
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    // eslint-disable-next-line no-console
    console.info('[firebase] Using local Firestore + Functions emulators.');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[firebase] Emulator connection failed:', e?.message);
  }
}

// Call a callable Cloud Function and return its `.data` payload directly.
// Firebase attaches the signed-in user's auth token automatically, so the
// function can trust request.auth (see requireMeltplan in functions/index.js).
async function callFunction(name, payload) {
  const fn = httpsCallable(functions, name);
  const res = await fn(payload);
  return res.data;
}

// Keep sessions across reloads/tabs
setPersistence(auth, browserLocalPersistence).catch(() => {});

const googleProvider = new GoogleAuthProvider();
// (UI hint only; we still enforce domain in the app)
googleProvider.setCustomParameters({ 
  hd: 'meltplan.com', 
  prompt: 'select_account',
  // This helps brand the OAuth screen
  login_hint: 'user@meltplan.com'
});

// Prefer popup; fall back to redirect if blocked
async function signInWithGoogle() {
  try {
    await signInWithPopup(auth, googleProvider);
  } catch (err) {
    // Most corporate setups block popups; redirect is more reliable
    if (err?.code === 'auth/popup-blocked') {
      await signInWithRedirect(auth, googleProvider);
    } else {
      await signInWithRedirect(auth, googleProvider);
    }
  }
}

async function signOutUser() {
  try { await _signOut(auth); } catch {}
}

// Upload image to Firebase Storage and return download URL
async function uploadImageToStorage(file, path = 'signature-images') {
  try {
    // Create a unique filename with timestamp
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `${timestamp}.${fileExtension}`;
    
    // Create storage reference
    const storageRef = ref(storage, `${path}/${fileName}`);
    
    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
}

// Convenience wrappers so the app doesn’t pass `auth` around
const getRedirectResult = () => _getRedirectResult(auth);
const onAuthStateChanged = (cb) => _onAuthStateChanged(auth, cb);

export {
  app,
  auth,
  storage,
  db,
  functions,
  callFunction,
  googleProvider,
  signInWithGoogle,
  signOutUser,
  getRedirectResult,
  onAuthStateChanged,
  uploadImageToStorage,
};